import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Shield, Star, Check, Trophy } from 'lucide-react';
import { useEffect, useState } from 'react';

const Quality = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [currentCertIndex, setCurrentCertIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCertIndex((prevIndex) => 
        prevIndex === certifications.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change certification every 5 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  const certifications = [
    {
      name: "IATF 16949:2016",
      description: "Automotive Quality Management System Standard for the design, development, production, and service of automotive-related products",
      year: "2023",
      color: 'from-purple-600 to-purple-500',
      bgColor: 'from-purple-600/10 to-purple-500/5'
    },
    {
      name: "ISO 14001:2015", 
      description: "Environmental Management System Standard for effective environmental management and sustainable operations",
      year: "2023",
      color: 'from-green-600 to-green-500',
      bgColor: 'from-green-600/10 to-green-500/5'
    },
    {
      name: "ISO 45001:2018",
      description: "Occupational Health and Safety Management System Standard for ensuring a safe and healthy workplace",
      year: "2023",
      color: 'from-blue-600 to-blue-500',
      bgColor: 'from-blue-600/10 to-blue-500/5'
    }
  ];

  const certificationImages = [
    "/16969.jpg",
    "/14001.jpg",
    "/45001.jpg"
  ];

  const qualityPoints = [
    t('quality.point1'),
    t('quality.point2'),
    t('quality.point3'),
    t('quality.point4'),
    t('quality.point5'),
    t('quality.point6')
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="quality" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-block"
          >
            <Badge className="mb-6 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white border-0">
              <Star className="w-4 h-4 mr-2" />
              {t('quality.badge')}
            </Badge>
          </motion.div>
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            {t('quality.title')}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            {t('quality.description')}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Ľavá časť - Vymenované certifikáty */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              {t('quality.certifications.title')}
            </h3>
            <motion.div 
              className="grid grid-cols-1 gap-6"
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  className="group cursor-pointer"
                  onClick={() => setCurrentCertIndex(index)}
                >
                  <Card className={`border-2 transition-all duration-300 ${
                    currentCertIndex === index 
                      ? 'border-blue-500 shadow-lg bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 shadow-md'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div 
                          className={`w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center text-white text-lg font-bold ${
                            cert.color.replace('from-', 'bg-gradient-to-br from-').replace(' to-', ' to-')
                          }`}
                        >
                          {cert.name.split(' ')[0].charAt(0)}
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {cert.name}
                          </h4>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Pravá časť - Obrázky certifikátov s názvom na celú šírku */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="space-y-6"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentCertIndex}
                className="bg-gradient-to-br from-blue-600/10 via-purple-600/5 to-green-600/5 rounded-3xl p-8 shadow-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                {/* Názov certifikátu na celú šírku */}
                <motion.h3 
                  className="text-3xl font-bold text-gray-900 mb-6 text-center w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {certifications[currentCertIndex].name}
                </motion.h3>
                
                {/* Obrázok certifikátu */}
                <motion.div 
                  className="h-64 w-full flex items-center justify-center mb-6"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <img 
                    src={certificationImages[currentCertIndex]}
                    alt={certifications[currentCertIndex].name}
                    className="h-full w-auto object-contain rounded-lg shadow-lg"
                  />
                </motion.div>
                
                {/* Popis certifikátu */}
                <motion.p 
                  className="text-gray-600 text-center leading-relaxed text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  {certifications[currentCertIndex].description}
                </motion.p>
              </motion.div>
            </AnimatePresence>
            
            {/* Navigačné tlačidlá */}
            <div className="flex justify-center space-x-3">
              {certifications.map((cert, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentCertIndex(index)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    index === currentCertIndex 
                      ? 'bg-blue-600 text-white shadow-lg' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  aria-label={`Zobraziť certifikát ${cert.name}`}
                >
                  {cert.name}
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 relative overflow-hidden"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1, duration: 0.8 }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,theme(colors.green.500/0.1),transparent_70%)]"></div>
          
          <motion.h3 
            className="text-3xl font-bold text-white mb-8 text-center relative z-10"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1.2, duration: 0.5 }}
          >
            {t('quality.commitment.title')}
          </motion.h3>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            {qualityPoints.map((point, index) => (
              <motion.div 
                key={index} 
                className="flex items-center gap-4 p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <Check className="w-6 h-6 text-green-400 flex-shrink-0" />
                <span className="text-gray-200 font-medium">{point}</span>
              </motion.div>
            ))}
          </motion.div>
          
          <div className="absolute top-6 right-6 opacity-10">
            <Star className="w-16 h-16 text-white" />
          </div>
          <div className="absolute bottom-6 left-6 opacity-10">
            <Trophy className="w-12 h-12 text-white" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Quality;